<template>
  <div class="ugcentrypoint-chooseimage">
    <div class="ugcentrypoint-chooseimage-input-wrapper">
      <input
        @change="setUGCImage"
        :id="inputId"
        class="hide ugc-image-source"
        type="file"
        accept="image/jpg,image/jpeg,image/png"
        :name="inputId"
        :ref="inputId"
      />
      <div class="ugcentrypoint-button-ugc-upload-wrapper">
        <button
          class="ugc-uploader-label button ugcentrypoint-button"
          :disabled="disabled"
          @click="onUploadClick"
        >
          <span class="icon icon-camera"></span>&nbsp;
          {{ translations[inputLabel || 'ugcEPChooseImage'] }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>

import { sessionKeys } from '@/data/constants';
import { validateImageRequirements } from '@/utils/imageValidator.utils';

export default {
  name: "chooseImage",
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    inputId: {
      type: String,
      default: "6f968b40-bc06-4deb-9b84-d0c21554be14"
    },
    inputLabel: {
      type: String,
      default: null,
    },
  },
  computed: {
    translations() {
      return this.$store.state.translations.data;
    },
    configuration() {
      return this.$store.state.utils.configuration;
    },
    checkerboarderActive() {
      return this.$store.state.UGCEntryPoint.checkerboarder.active;
    },
    enablePerformanceLogging() {
      return this.$store.state.utils.queryParams.enablePerformanceLogging || this.$store.state.utils.configuration.enablePerformanceLogging;
    },
    initialTime() {
      return this.$store.state.utils.initialTime;
    },
    isApparelProduct(){
      const isApparel = this.$store.getters['utils/isApparelEnabled'];
      return this.$store.state.UGCEntryPoint.productType === "apparel" || isApparel;
    },
  },
  methods: {
    /**
     * @function onUploadClick
     * Handle click on "Choose Image" button. Clicks hidden input of type "file".
     * @returns {undefined}
     */
    onUploadClick(firstLoad) {
      this.$refs[this.inputId].click();

      if (firstLoad === true) {
        return;
      }

      if (this.checkerboarderActive) {
        this.$ugcEntryPointDataLayer.logAnalyticsEvent({
          action: 'checkerboarder',
          type: this.$ugcEntryPointDataLayer.types.addYourImage
        });
       } else {
        this.$ugcEntryPointDataLayer.logAnalyticsEvent({
          action: 'ugc',
          type: this.$ugcEntryPointDataLayer.types.chooseImage
        });
      }
    },

    /**
     * @function setUGCImage
     * @param {object} event Event object with attribute type "change".
     * Grabs the image from the input field and sends it to validateImageProperties()
     * @returns {undefined}
     */
    setUGCImage(event) {
      const file = event.target.files[0];
      const image = new Image();

      image.onload = (() => {
        this.$store.commit('UGCEntryPoint/SAVE_DIMENSIONS_OF_UPLOADED_IMAGE', [image.width, image.height]);

        const { imageStatus, isValidImage } = validateImageRequirements(file,image);

        if (!isValidImage) {
          this.showUploadError(imageStatus);
        } else {
          this.uploadImage(file);
        }

      }).bind(this);
      const createObjectURL = (window.URL || window.webkitURL || {}).createObjectURL || function () { };
      const imageURL = createObjectURL(file);
      image.src = imageURL;

      if (this.$store.state.UGCEntryPoint.checkerboarder.active) {
        this.$store.commit("UGCEntryPoint/UPDATE_CHECKERBOARDER", {
          originalImageUrl: imageURL,
          originalImageFile: file,
        });
      }
    },

    showUploadError(imageStatus) {
      this.$store.commit("UGCEntryPoint/SET_ACTIVE_VIEW", this.configuration.modals.ugcErrorImg ? 'ugcentrypointUploadErrorImage' : 'ugcentrypointUploadError');
      this.$store.commit("UGCEntryPoint/SET_IMAGE_STATUS", imageStatus);
      if (!this.checkerboarderActive) {
        this.$ugcEntryPointDataLayer.logAnalyticsEvent({
          action: 'ugc',
          type: this.$ugcEntryPointDataLayer.types.uploadError
        });
      }
    },

    /**
     * @function uploadImage
     * @param {object} inputFile Browser-generated object for a file to upload.
     * @param {string} inputFile.name Example "example.png"
     * @param {string} inputFile.type Example "image/png"
     * @param {string} inputFile.lastModified Example 1541522954009
     * @param {string} inputFile.size Example 6182
     * Sends a signal to the Store to upload the image to the server.
     * Then adds, to the Vuex store, the URL of the uploaded image which came back with the server response.
     */
    uploadImage(inputFile) {
      this.$store.commit(
        "UGCEntryPoint/SET_ACTIVE_VIEW",
        this.configuration.modals?.ugcUploading ? "ugcentrypointImageUploading" : "ugcentrypointFileUploading"
      );
      if(this.enablePerformanceLogging)
            console.log("Image Upload Started: ", new Date().getTime() / 1000 - this.initialTime);
      this.$store
        .dispatch("UGCEntryPoint/UPLOAD", { inputFile })
        .then(data => {
          if(this.enablePerformanceLogging)
            console.log("Image Uploaded: ", new Date().getTime() / 1000 - this.initialTime);

          this.$cookies.set(sessionKeys.ugcContactDetailsFilled, this.$store.state.UGCEntryPoint.contactDetails, 0);
          this.$cookies.set(sessionKeys.ugcTermsConditionsAccepted, true, 0);
          if (this.$store.state.UGCEntryPoint.optIn) {
            this.$cookies.set(sessionKeys.ugcOptIn, this.$store.state.UGCEntryPoint.optIn, 0);
          }
          this.$store.commit('UGCEntryPoint/SAVE_ADDRESS_OF_UPLOADED_IMAGE', data);
          this.$store.commit('UGCEntryPoint/SAVE_TYPE_OF_UPLOADED_IMAGE', data.ugc_image_path.split(".").pop());
          this.$store.commit('UGCEntryPoint/SET_HAS_ACCEPTED_TERMS_AND_CONDITIONS', true);
          this.$eventBus.$emit("ugc-uploaded");
        })
        .catch(() => {
          this.showUploadError();
        });
    }
  },
  mounted() {
    if (
      this.$cookies.get(sessionKeys.ugcTermsConditionsAccepted)
      && (this.$cookies.get(sessionKeys.ugcContactDetailsFilled) || !this.$store.state.utils.configuration.ugc.enableUGCContactDetails)
      && !this.$store.state.utils.configuration.ugc.forceUGCEntryPointTC
    )
      this.onUploadClick(true);
  }
}
</script>
